import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ErrorState } from "@arcadeai/design-system/components/ui/molecules/error-state";
import { Home, RotateCcw } from "lucide-react";
import { Component, type ReactNode } from "react";
import { deriveHomePath } from "@/lib/org-project-context/home-path";

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo,
    });

    this.props.onError?.(error, errorInfo);
  }

  private readonly handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private readonly handleTryAgain = () => {
    this.handleReset();
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      const stackTrace = [
        this.state.error.stack,
        this.state.errorInfo?.componentStack
          ? `\n\nComponent Stack:${this.state.errorInfo.componentStack}`
          : "",
      ]
        .filter(Boolean)
        .join("");

      const homePath = deriveHomePath(
        typeof window === "undefined" ? "/" : window.location.pathname
      );

      return (
        <ErrorState
          className="h-svh bg-background"
          errorMessage={this.state.error.message || "An unknown error occurred"}
          errorStack={stackTrace}
        >
          <Button
            className="flex-1"
            onClick={this.handleTryAgain}
            variant="outline"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button
            className="flex-1"
            // This boundary renders outside the RouterProvider, so a router Link
            // would have no router context; use a plain anchor with a full reload.
            // biome-ignore lint/a11y/useAnchorContent: Button children are cloned into the anchor via Base UI render prop
            render={<a aria-label="Go Home" href={homePath} />}
          >
            <Home className="mr-2 h-4 w-4" />
            Go Home
          </Button>
        </ErrorState>
      );
    }

    return this.props.children;
  }
}
